<script setup>
    import { ref, onMounted, onUnmounted, watch, computed, defineProps } from 'vue';
    import { utcToLocal } from '@/tools/TimeFunctions';
    import Plotly from 'plotly.js-dist';

    const props = defineProps({
        template: Object,
        dataID: String,
        streamData: {
            type: Object,
            default: () => ({})
        }
    });

    const chartContainer = ref(null);
    const lastUpdate = ref('Last Update:');
    let resizeObserver = null;
    let resizeTimeout = null;

    // Computed property to get the latest data for this widget's source
    const sourceData = computed(() => {
        if (!props.template?.source) return null;
        return props.streamData[props.template.source]?.lastValue || null;
    });

    // Computed property for formatted last update time
    const formattedLastUpdate = computed(() => {
        if (!props.template?.source) return 'No source';
        const data = props.streamData[props.template.source];
        if (data?.lastUpdate) {
            return `Last Update: ${utcToLocal(data.lastUpdate, '12hour')}`;
        }
        return 'Waiting for data...';
    });

    const createGaugeData = () => {
        const containerWidth = chartContainer.value?.offsetWidth || 300;
        const isSmall = containerWidth < 300;
        return [{
        type: "indicator",
        mode: "gauge+number",
        value: null,
        number: {
            prefix: ``,
            suffix: `<span style="font-size: 0.8em;">${props.template.unit || ''}</span> `,
            font: { 
                size: isSmall ? 24 : 40,
                family: 'Inter, system-ui, sans-serif'
            },
            valueformat: props.template.valuePrecision || '.2f',
        },
        title: { 
            text: props.template.title, 
            font: { 
                size: isSmall ? 14 : 24,
                family: 'Inter, system-ui, sans-serif'
            } 
        },
        gauge: {
            axis: { 
                range: null,
                tickwidth: 1,
                tickcolor: "currentColor"
            },
            bar: { color: props.template.color },
            bgcolor: props.template.bgColor || 'white',
            borderwidth: 2,
            bordercolor: "gray",
            steps: []
        }
    }]};

    const createLayout = () => {
        if (!chartContainer.value) return null;
        
        const containerWidth = chartContainer.value.offsetWidth;
        if (!containerWidth) return null;

        const isSmall = containerWidth < 300;
        return {
            width: containerWidth,
            height: isSmall ? 180 : 250,
            margin: { t: 80, r: 25, l: 25, b: 25 },
            paper_bgcolor: "transparent",
            font: { 
                color: "currentColor",
                family: 'Inter, system-ui, sans-serif'
            }
        };
    };

    const config = {
        displaylogo: false,
        responsive: true,
        staticPlot: true
    };

    const updateChart = () => {
        if (!chartContainer.value) return;
        
        const layout = createLayout();
        if (!layout) {
            // If layout is null, try again in the next frame when dimensions might be available
            requestAnimationFrame(updateChart);
            return;
        }

        const data = createGaugeData();

        // Set Prefix
        if (props.template.prefix) {
            data[0].number.prefix = `<span style="font-size: 0.6em;">${props.template.prefix}</span><br>`;
        }

        // Set the range
        if (Array.isArray(props.template.range)) {
            data[0].gauge.axis.range = props.template.range;
        }

        // Set the steps
        if (props.template.steps) {
            data[0].gauge.steps = props.template.steps.map(step => ({
                range: Array.isArray(step.range) ? step.range : [null, null],
                color: step.color,
                thickness: `${step.thickness || 1}`
            }));
        }

        // Set tick Intervals - calculate dynamically based on current range
        const interval = ((props.template.range[1] - props.template.range[0]) / 6);
        const tickvals = Array.from({ length: 7 }, (_, i) => {
            const value = props.template.range[0] + interval * i;
            return interval < 1 ? parseFloat(value.toFixed(2)) : Math.round(value);
        });
        data[0].gauge.axis.tickvals = tickvals;

        // Set delta
        if (props.template.delta) {
            data[0].mode = "gauge+number+delta";
            data[0].delta = props.template.delta;
        }

        // Set threshold
        if (props.template.threshold) {
            data[0].gauge.threshold = {
                line: { color: "currentColor", width: 4 },
                thickness: 1,
                value: props.template.threshold
            };
        }

        try {
            Plotly.newPlot(chartContainer.value, data, layout, config);
        } catch (error) {
            console.error('Error plotting gauge:', error);
            // Try again in the next frame
            requestAnimationFrame(updateChart);
        }
    };

    const updateValue = (docData) => {
        if (!chartContainer.value || !docData) return;

        let value;
        if (props.template.field) {
            value = parseFloat(docData[props.template.field]);
        } else if (props.template.calculate) {
            const { formula, denominator } = props.template.calculate;
            const variables = {};
            denominator.forEach((field) => {
                variables[field] = parseFloat(docData[field]);
            });
            const calculateFn = new Function(...denominator, `return ${formula}`);
            value = calculateFn(...Object.values(variables));
        }

        if (!isNaN(value)) {
            try {
                Plotly.restyle(chartContainer.value, { 'value': [value] });
            } catch (error) {
                console.error('Error updating gauge value:', error);
            }
        }
    };

    // Watch for changes in source data
    watch(sourceData, (newData) => {
        if (newData) {
            updateValue(newData);
        }
    }, { deep: true });

    // Watch for changes in formatted last update
    watch(formattedLastUpdate, (newUpdate) => {
        lastUpdate.value = newUpdate;
    });

    // Debounced version of updateChart
    const debouncedUpdateChart = () => {
        if (resizeTimeout) {
            clearTimeout(resizeTimeout);
        }
        resizeTimeout = setTimeout(() => {
            if (chartContainer.value) {
                const currentValue = chartContainer.value._fullData?.[0]?.value ?? null;
                updateChart();
                if (currentValue !== null && !isNaN(currentValue)) {
                    setTimeout(() => {
                        if (chartContainer.value) {
                            try {
                                Plotly.restyle(chartContainer.value, { 'value': [currentValue] });
                            } catch (error) {
                                console.error('Error restoring value after resize:', error);
                            }
                        }
                    }, 50);
                }
            }
        }, 250);
    };

    // Watch for template changes in preview mode
    watch(() => props.template, (newTemplate, oldTemplate) => {
        console.log(oldTemplate);
        if (newTemplate.value !== undefined && newTemplate.value !== null) {
            // Preview mode: update chart with new template settings
            updateChart();
            setTimeout(() => {
                if (chartContainer.value) {
                    try {
                        Plotly.restyle(chartContainer.value, { 'value': [newTemplate.value] });
                    } catch (error) {
                        console.error('Error updating preview value:', error);
                    }
                }
            }, 100);
        } else {
            // Normal mode: just update the chart with new styling/config
            updateChart();
        }
    }, { deep: true });

    onMounted(() => {
        // If a direct value is provided in template, use it (preview mode)
        if (props.template.value !== undefined && props.template.value !== null) {
            updateChart();
            // Update with the provided value
            setTimeout(() => {
                if (chartContainer.value) {
                    try {
                        Plotly.restyle(chartContainer.value, { 'value': [props.template.value] });
                    } catch (error) {
                        console.error('Error setting preview value:', error);
                    }
                }
            }, 100);
            lastUpdate.value = 'Preview Mode';
        } else {
            // Normal mode: chart will update via streamData watcher
            updateChart();
            
            // Initialize with existing stream data if available
            if (sourceData.value) {
                updateValue(sourceData.value);
                lastUpdate.value = formattedLastUpdate.value;
            }
        }

        // Set up resize observer with debounced handler
        resizeObserver = new ResizeObserver(() => {
            debouncedUpdateChart();
        });
        
        if (chartContainer.value) {
            resizeObserver.observe(chartContainer.value);
        }
    });

    onUnmounted(() => {
        if (resizeTimeout) {
            clearTimeout(resizeTimeout);
        }
        if (resizeObserver && chartContainer.value) {
            resizeObserver.unobserve(chartContainer.value);
            resizeObserver.disconnect();
        }
        if (chartContainer.value) {
            Plotly.purge(chartContainer.value);
        }
    });
</script>

<template>
    <div class="bg-white dark:bg-dark rounded-lg p-4 w-full">
        <div ref="chartContainer" class="w-full"></div>
        <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">{{ formattedLastUpdate }}</p>
    </div>
</template>